import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme } from '@mui/material';
import { ErrorCircle } from 'ui/ErrorCircle';
import { ButtonV2 } from 'ui/ButtonV2';
import { useAuth } from 'lib/useAuth';
import { REDUCED_CONTENT_MAX_WIDTH } from 'config/constants';

const PREFIX = 'PayCardCheckError';

const classes = {
    title: `${PREFIX}title`,
    icon: `${PREFIX}icon`,
    buttonContainer: `${PREFIX}buttonContainer`
};

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    [`& .${classes.title}`]: {
        fontSize: '24px',
        fontWeight: 500,
        color: theme.palette.text.primary
    },

    [`& .${classes.icon}`]: {
        height: 100,
        width: 100
    },

    [`& .${classes.buttonContainer}`]: {
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: REDUCED_CONTENT_MAX_WIDTH,
        right: 0,
        padding: theme.spacing(1)
    }
}));

interface IProps {
    errorMessage?: string | null;
    onSignIn: () => void;
    onAddPaymentMethod: () => void;
}

export const PayCardCheckError: React.FC<React.PropsWithChildren<IProps>> = ({
    errorMessage,
    onSignIn,
    onAddPaymentMethod
}) => {
    const { t } = useTranslation();
    const { isGuest } = useAuth();
    const theme = useTheme();

    return (
        <StyledBox>
            <Typography className={classes.title}>{t('PAT_QUICKPAY_ERROR_TITLE')}</Typography>
            {isGuest && (
                <Box paddingX={1}>
                    <Typography align="center">{errorMessage}</Typography>
                </Box>
            )}
            {!isGuest && (
                <Box paddingX={1}>
                    <Typography align="center">{t('PAT_PAYMENT_CARD_REQUIRED')}</Typography>
                </Box>
            )}
            <Box paddingTop={5}>
                <ErrorCircle className={classes.icon} color={theme.palette.primary.main} />
            </Box>
            {isGuest && (
                <div className={classes.buttonContainer}>
                    <ButtonV2 fullWidth onClick={onSignIn}>
                        {t('ONBOARDING_SIGN_IN')}
                    </ButtonV2>
                </div>
            )}
            {!isGuest && (
                <div className={classes.buttonContainer}>
                    <ButtonV2 fullWidth onClick={onAddPaymentMethod}>
                        {t('BUTTON_ADD_CARD')}
                    </ButtonV2>
                </div>
            )}
        </StyledBox>
    );
};
